<script lang="ts">
  import { run } from "svelte/legacy";

  import { useQueryClient } from "@sveltestack/svelte-query";
  import { useGetInvoiceMethods } from "../../helpers/api";
  import type { Invoice, Person, TInvoiceMethodsResponse } from "../../helpers/types";
  import { validateInvoice } from "../../helpers/validators";
  import Card from "../atoms/Card.svelte";
  import Checkbox from "../atoms/Checkbox.svelte";
  import ErrorText from "../atoms/ErrorText.svelte";
  import Heading from "../atoms/Heading.svelte";
  import Indent from "../atoms/Indent.svelte";
  import Loader from "../atoms/Loader.svelte";
  import PhoneInput from "../atoms/PhoneInput.svelte";
  import Text from "../atoms/Text.svelte";
  import TextInput from "../atoms/TextInput.svelte";
  import Error from "./Error.svelte";
  import { onMount } from "svelte";
  import { useRendered } from "../../helpers/hooks";

  interface Props {
    campaign: string;
    selectedInvoice: Invoice;
    billingEmail: string | undefined;
    person: Person;
    personMobile: string | undefined;
    valid: boolean;
    disabled?: boolean;
    isInvoiceSelected: boolean;
  }

  let {
    campaign,
    selectedInvoice = $bindable(),
    billingEmail,
    person,
    personMobile = $bindable(),
    valid = $bindable(),
    disabled = false,
    isInvoiceSelected,
  }: Props = $props();

  let { rendered } = useRendered();

  const client = useQueryClient();

  const checkSelectedInvoice = (methodsData: TInvoiceMethodsResponse) => {
    if (isInvoiceSelected && billingEmail) return;
    if (methodsData?.result) {
      for (let i = 0; i < methodsData.result.length; i++) {
        let method = methodsData.result[i];
        if (method.label === "E-postadress") {
          selectedInvoice = {
            description: method.description,
            label: method.label,
            id: method.id,
            value: billingEmail,
          };
          return;
        }
      }
    }
  };

  const formatMobile = (mobile: string): string => {
    if (mobile && mobile.charAt(0) !== "+") {
      return "+46" + (mobile.charAt(0) == "0" ? mobile.substring(1) : mobile);
    } else {
      return mobile;
    }
  };

  let params = {
    code: campaign,
  };

  let invoiceResponse: TInvoiceMethodsResponse | undefined = $state();

  const queryResult = useGetInvoiceMethods(params, {
    staleTime: 10000,
    refetchOnWindowFocus: false,
    enabled: $rendered === true && !disabled,
    onSuccess: (data) => {
      invoiceResponse = data;
      checkSelectedInvoice(data);
    },
  });

  if (client.getQueryData(["invoicemethods", params])) {
    invoiceResponse = client.getQueryData<TInvoiceMethodsResponse>(["invoicemethods", params]);
    // svelte-ignore state_referenced_locally
    if (invoiceResponse) {
      checkSelectedInvoice(invoiceResponse);
    }
  }

  onMount(() => {
    if (!client.getQueryData(["invoicemethods", params])) $queryResult.refetch();
  });

  $effect(() => {
    if (invoiceResponse && ((!disabled && invoiceResponse?.result) || person.email)) {
      checkSelectedInvoice(invoiceResponse);
    }
  });

  $effect(() => {
    valid = validateInvoice(selectedInvoice);
  });
</script>

{#if $queryResult.status == "error"}
  <Error
    onClick={() => {
      $queryResult.refetch();
    }}
    >Något gick fel vid hämtning av betalningsmetoder
  </Error>
{:else if $queryResult.status == "loading"}
  <Card class="h-60 w-full flex justify-center items-center">
    <Loader />
  </Card>
{:else if $queryResult.status == "success"}
  <Card class={disabled ? "opacity-40 cursor-not-allowed" : ""}>
    <Heading>Hur vill du ha din faktura?</Heading>
    {#each $queryResult.data.result as method}
      <div class="mt-3">
        {#if method.label == "E-postadress"}
          <Checkbox
            type={"radio"}
            bind:group={selectedInvoice}
            value={{
              description: method.description,
              label: method.label,
              id: method.id,
              value: billingEmail,
            }}
            {disabled}
            checked={selectedInvoice && selectedInvoice.id === method.id && !disabled}
          >
            {method.description}
          </Checkbox>
          {#if selectedInvoice && selectedInvoice.id === method.id && !disabled}
            <Indent>
              <div class="flex flex-col space-y-2">
                <TextInput
                  id="billingEmail"
                  name="billingEmail"
                  class="max-w-sm w-full fade-in"
                  error={!valid}
                  bind:value={selectedInvoice.value}
                />
              </div>
              {#if !valid}
                <ErrorText>Fyll i giltig e-postadress</ErrorText>
              {/if}
            </Indent>
          {/if}
        {:else if method.label == "Mobilnummer"}
          <Checkbox
            type={"radio"}
            {disabled}
            bind:group={selectedInvoice}
            value={{
              description: method.description,
              label: method.label,
              id: method.id,
              value: formatMobile(personMobile ?? ""),
            }}
            checked={selectedInvoice && selectedInvoice.id === method.id && !disabled}
          >
            {method.description}
          </Checkbox>
          {#if selectedInvoice && selectedInvoice.id === method.id && !disabled}
            <Indent class="fade-in">
              <Text
                >Fungerar endast för bankerna SEB, Handelsbanken, Swedbank, Danske Bank och Nordea.
                Ange mobilnumret i internationellt format (ex +4670.......)</Text
              >
              <PhoneInput error={!valid} bind:value={selectedInvoice.value} name={"phone"} />
              {#if !valid}
                <ErrorText>Fyll i mobilnummer</ErrorText>
              {/if}
            </Indent>
          {/if}
        {:else if method.label == "Fakturatyp"}
          <Checkbox
            type={"radio"}
            bind:group={selectedInvoice}
            value={{
              description: method.description,
              label: method.label,
              id: method.id,
              value: undefined,
            }}
            {disabled}
            checked={selectedInvoice && selectedInvoice.id === method.id && !disabled}
          >
            {method.description}
          </Checkbox>
          {#if selectedInvoice && selectedInvoice.id === method.id}
            <Indent class="fade-in">
              <Text
                >Du får fakturan med posten till din brevlåda. Om du är ansluten till den digitala
                brevlådan Kivra kommer fakturan dit istället</Text
              >
            </Indent>
          {/if}
        {/if}
      </div>
    {/each}
  </Card>
{/if}
